import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionPanel } from "@angular/material/expansion";
import { LoggingService, PromptConfirmDialogComponent, PromptConfirmInput, PromptConfirmResult } from "@merri/common";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/core";
import { EventContextService, EventVenuesService } from "src/app/features/events";
import { HelperMethods } from "src/app/shared";
import { Category, SubCategory, VisionboardItem } from "../../../models";
import {
  VisionboardAttributesService,
  VisionboardCategoriesService,
  VisionboardFavoritesDialogService,
  VisionboardItemCompatibilityService,
  VisionboardService,
  VisionboardWorkspaceItemsStoreService,
} from "../../../services";

const ACTIVE_CATEGORY_CLASS = "active-sub-category";

@Component({
  selector: "app-visionboard-categories",
  templateUrl: "./visionboard-categories.component.html",
  styleUrls: ["./visionboard-categories.component.scss"],
  standalone: false,
})
export class VisionboardCategoriesComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  private selected: [Category, SubCategory];

  private categoryClasses: { [categoryKey: string]: string[] } = {};

  @Input() hideControls: boolean;

  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  categories: Category[];
  headerHeight: string = "64px";
  showRequestButton: boolean;
  showControls: boolean;
  eventVenueId: number;

  constructor(
    private visionboardCategoriesService: VisionboardCategoriesService,
    private visionboardAttributesService: VisionboardAttributesService,
    private visionboardItemCompatibilityService: VisionboardItemCompatibilityService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardService: VisionboardService,
    private matDialog: MatDialog,
    private sessionService: SessionService,
    private _eventContextService: EventContextService,
    private _visionboardFavoritesDialogService: VisionboardFavoritesDialogService,
    private _eventVenuesService: EventVenuesService,
  ) {}

  ngOnInit(): void {
    this.visionboardCategoriesService.selectedCategory$.pipe(takeUntil(this.destroyed$)).subscribe(selected => {
      if (this.selected) {
        const key = this.getSubCategoryKey(this.selected[0], this.selected[1]);
        if (this.categoryClasses[key]) {
          HelperMethods.removeItem(this.categoryClasses[key], ACTIVE_CATEGORY_CLASS);
        }
      }

      this.selected = selected;

      if (this.selected) {
        const key = this.getSubCategoryKey(this.selected[0], this.selected[1]);
        if (this.categoryClasses[key]) {
          this.categoryClasses[key].push(ACTIVE_CATEGORY_CLASS);
        } else {
          this.categoryClasses[key] = [ACTIVE_CATEGORY_CLASS];
        }
      }
    });

    this.eventVenueId = this._eventVenuesService.getFirstVenue()?.id;

    this.visionboardCategoriesService.currentCategories$.pipe(takeUntil(this.destroyed$)).subscribe(categories => {
      this.categories = categories.categories;

      this.setCategoryClasses();

      this.closeAllPanels();
    });

    this.visionboardService.mode$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.closeAllPanels();
    });

    this.visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.setCategoryClasses());

    this.visionboardWorkspaceItemsStoreService.itemAdded$.pipe(takeUntil(this.destroyed$)).subscribe(item => this.handleItemAdded(item));

    this.sessionService.sessionUser$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.showRequestButton = this.sessionService.canMakeCustomRequests();
        this.showControls = !this.hideControls && !this._eventContextService.isGuestEvent();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.visionboardCategoriesService.setSelectedCategory(null);
  }

  getButtonClasses(category: Category, subCategory: SubCategory): string[] {
    const key = this.getSubCategoryKey(category, subCategory);

    return this.categoryClasses[key] || [];
  }

  onSubCategoryClick(category: Category, subCategory: SubCategory) {
    const selected = this.visionboardCategoriesService.getSelectedCategory();

    if (selected && selected[0] === category && selected[1] === subCategory) {
      this.visionboardCategoriesService.setSelectedCategory(null);
    } else {
      const parentFilters = this.visionboardItemCompatibilityService.getParentAttributeTypeValues(category.name, subCategory.name);

      this.visionboardAttributesService.setTempAttributeOverridesByValues("Type", parentFilters);

      this.visionboardCategoriesService.setSelectedCategory([category, subCategory]);
    }
  }

  onCategoryPanelClosed(): void {
    this.visionboardCategoriesService.setSelectedCategory(null);
  }

  getSubCategoryKey(category: Category, subCategory: SubCategory): string {
    return this.getSubCategoryKeyInner(category.name, subCategory.name);
  }

  private handleItemAdded(item: VisionboardItem): void {
    /**
     * If adding an item from the favorites modal, we will handle it there in order to deal with the "compatible items"
     * scenario.  Therefore if an item is added by way of the favorites modal, we want to suppress this handler.
     */
    if (this._visionboardFavoritesDialogService.isOpen()) {
      return;
    }

    if (this.visionboardItemCompatibilityService.requiresChildItems(item)) {
      const dialog = this.matDialog.open(PromptConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: "Item Recommendation",
          message: this.visionboardItemCompatibilityService.getChildRecommendedPrompt(item.category, item.subCategory),
        } as PromptConfirmInput,
      });

      dialog
        .afterClosed()
        .pipe(take(1))
        .subscribe((result: PromptConfirmResult) => {
          LoggingService.log(`VisionboardCategoriesComponent - compatible dialog closed - ${PromptConfirmResult[result]}`);

          if (result === PromptConfirmResult.Yes) {
            this.visionboardAttributesService.setTempAttributeOverridesByValues("Type", item.attributeTypeValues);

            const childCategory = this.visionboardItemCompatibilityService.getChildCategory(item);
            this.visionboardCategoriesService.setSelectedCategoryBySubCategory(childCategory);

            this.setSubCategoryFocus(childCategory[0], childCategory[1]);
          }
        });
    } else if (item.resizable) {
      this.visionboardService.showResizablePrompt$(this.matDialog).subscribe();
    }
  }

  private setCategoryClasses(): void {
    this.categoryClasses = {};

    if (this.categories) {
      this.categories.forEach(category => {
        category.subCategories.forEach(subCategory => {
          const key = this.getSubCategoryKey(category, subCategory);

          this.categoryClasses[key] = [];

          if (this.selected && this.selected[0] === category && this.selected[1] === subCategory) {
            this.categoryClasses[key].push(ACTIVE_CATEGORY_CLASS);
          }

          if (!this.visionboardItemCompatibilityService.canSelectChildItems(category.name, subCategory.name)) {
            this.categoryClasses[key].push("disabled");

            if (this.selected && this.selected[0] === category && this.selected[1] === subCategory) {
              this.visionboardCategoriesService.setSelectedCategory(null);
            }
          }
        });
      });
    }
  }

  private closeAllPanels(): void {
    if (this.panels) {
      this.panels.forEach(i => i.close());
    }
  }

  private setSubCategoryFocus(category: string, subCategory: string): void {
    const key = this.getSubCategoryKeyInner(category, subCategory);

    if (key) {
      const element = document.getElementById(key);

      if (element) {
        element.focus();
      }
    }
  }

  private getSubCategoryKeyInner(category: string, subCategory: string): string {
    return SubCategory.getKey(category, subCategory);
  }
}
